import React from 'react';
import Clock from '../../../assets/reusableImages/Clock.svg';

/**
 * CustomCard - A reusable card component with customizable content, labels, badges, and swap components
 * 
 * @param {Object} props
 * @param {string} props.width - Width of the card (default: w-full)
 * @param {string} props.className - Additional CSS classes to apply to the card
 * @param {boolean} props.showImage - Whether to show the image section
 * @param {string|Object} props.image - URL for the image or image object with src and alt
 * @param {boolean} props.showBadge - Whether to show the badge
 * @param {React.ReactNode} props.badgeComponent - Custom badge component to display
 * @param {boolean} props.showLabel1 - Whether to show the first label
 * @param {string} props.label1Text - Text for the first label
 * @param {boolean} props.showLabel2 - Whether to show the second label
 * @param {string} props.label2Text - Text for the second label
 * @param {string} props.title - Card title
 * @param {string} props.description - Card description
 * @param {boolean} props.showSwapComponent1 - Whether to show the first swap component
 * @param {React.ReactNode} props.swapComponent1 - Component for the first swap area
 * @param {boolean} props.showSwapComponent2 - Whether to show the second swap component
 * @param {React.ReactNode} props.swapComponent2 - Component for the second swap area
 * @param {boolean} props.showSwapComponent3 - Whether to show the third swap component
 * @param {React.ReactNode} props.swapComponent3 - Component for the third swap area
 * @param {boolean} props.showFooter - Whether to show the footer
 * @param {boolean} props.showDuration - Whether to show the duration in the footer
 * @param {string} props.durationText - Text for the duration
 * @param {boolean} props.showRight - Whether to show the right side of the footer
 * @param {boolean} props.showRating - Whether to show the rating in the footer
 * @param {string} props.ratingValue - Value for the rating
 * @param {boolean} props.showBookmark - Whether to show the bookmark button
 * @param {function} props.onBookmarkClick - Callback for bookmark button click
 */
const CustomCard = ({
  // Layout options
  width = "w-full",
  className = "",
  showImage = true,
  image = "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQcOraH8JCmYEC9CqPhqxK7SNsHtdsGeEHqrg&s",

  // Badge options
  showBadge = true,
  badgeComponent = null,

  // Label options
  showLabel1 = true,
  label1Text = "Label",
  showLabel2 = true,
  label2Text = "Label",

  // Content options
  title = "Title",
  description = "At vero eos et accusamus et iusto odio dignissimos ducimus qui blanditiis praesentium voluptatum deleniti atque corrupti quos dolores et quas.",

  // Swap component options
  showSwapComponent1 = true,
  swapComponent1 = null,
  showSwapComponent2 = true,
  swapComponent2 = null,
  showSwapComponent3 = true,
  swapComponent3 = null,

  // Footer options
  showFooter = true,
  showDuration = true,
  durationText = "4h 32min",
  showRight = true,
  showRating = true,
  ratingValue = "3.2",
  showBookmark = true,
  onBookmarkClick = () => { }
}) => {
  // Image handling exactly like ListItem
  const imgSrc = typeof image === "string" ? image : image.src;
  const imgAlt = typeof image === "string" ? "Card image" : image.alt || "Card image";

  // Determine if we should show any swap components
  const showAnySwapComponents = showSwapComponent1 || showSwapComponent2 || showSwapComponent3;

  return (
    <div className={`${width} relative bg-white rounded-xl outline outline-1 outline-offset-[-1px] outline-neutral-200 inline-flex flex-col justify-start items-start ${className}`}>
      {/* Image Section with same handling as ListItem */}
      {showImage && (
        <div className="self-stretch rounded-tl-xl rounded-tr-xl flex flex-col justify-start items-start h-[185.63px] overflow-hidden bg-gray-100 relative">
          <img
            src={imgSrc}
            alt={imgAlt}
            className="w-full h-full object-cover"
            crossOrigin="anonymous"
          />
        </div>
      )}

      {/* Badge (positioned absolutely) */}
      {showBadge && (
        <div className="absolute right-4 top-4">
          {badgeComponent}
        </div>
      )}

      {/* Content Section */}
      <div className="self-stretch p-6 flex flex-col justify-start items-start gap-3">
        {/* Title and Labels Section */}
        <div className="self-stretch flex flex-col justify-start items-start gap-2">
          {/* Labels Row */}
          {(showLabel1 || showLabel2) && (
            <div className="self-stretch inline-flex justify-start items-start gap-3">
              {showLabel1 && (
                <div className="flex justify-start items-start">
                  <div className="flex justify-start items-center gap-0.5">
                    <div className="w-4 h-4 relative overflow-hidden">
                      <div className="w-2.5 h-2.5 left-[2.67px] top-[2.67px] absolute bg-zinc-800" />
                    </div>
                    <div className="text-center justify-start text-zinc-800 text-sm font-circular font-[450]leading-normal">{label1Text}</div>
                  </div>
                </div>
              )}

              {showLabel2 && (
                <div className="flex justify-start items-start">
                  <div className="flex justify-start items-center gap-0.5">
                    <div className="w-4 h-4 relative overflow-hidden">
                      <div className="w-2.5 h-2.5 left-[2.67px] top-[2.67px] absolute bg-zinc-800" />
                    </div>
                    <div className="text-center justify-start text-zinc-800 text-sm font-circular font-[450]leading-normal">{label2Text}</div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Title - Moved outside the conditional */}
          <div className="self-stretch justify-start text-neutral-900 text-[20px] font-bold font-['Arial'] capitalize leading-9 truncate whitespace-nowrap overflow-hidden">{title}</div>
          {/* Description */}
          {description && (
            <div className="self-stretch justify-start text-neutral-900 text-sm font-normal font-['Arial'] leading-normal">
              {description}
            </div>
          )}
        </div>

        {/* Swap Components */}
        {showAnySwapComponents && (
          <div className="self-stretch flex flex-col gap-3">
            {showSwapComponent1 && swapComponent1 && (
              <div className="self-stretch">
                {swapComponent1}
              </div>
            )}

            {showSwapComponent2 && swapComponent2 && (
              <div className="self-stretch">
                {swapComponent2}
              </div>
            )}

            {showSwapComponent3 && swapComponent3 && (
              <div className="self-stretch">
                {swapComponent3}
              </div>
            )}
          </div>
        )}

        {/* Card Footer */}
        {showFooter && (
          <div className="self-stretch inline-flex justify-start items-center gap-4">
            {/* Duration */}
            {showDuration && (
              <div className="inline-flex flex-col justify-start items-start gap-6">
                <div className="self-stretch bg-blend-multiply bg-white flex flex-col justify-start items-start gap-2">
                  <div className="inline-flex justify-start items-center gap-1">
                    <img src={Clock} alt="Duration" className="w-4 h-4" />
                    <div className="justify-start text-zinc-800 text-sm font-circular font-[450]leading-normal">{durationText}</div>
                  </div>
                </div>
              </div>
            )}

            {/* Right Side of Footer (Rating and Bookmark) */}
            {showRight && (
              <div className="flex-1 flex justify-end items-center gap-4">
                {/* Rating */}
                {showRating && (
                  <div className="flex justify-center items-center gap-1">
                    <div className="w-6 h-6 relative">
                      <div className="w-4 h-4 left-[3.62px] top-[4px] absolute bg-amber-300" />
                    </div>
                    <div className="justify-start text-neutral-600 text-sm font-circular font-[450]leading-normal">{ratingValue}</div>
                  </div>
                )}

                {/* Bookmark */}
                {showBookmark && (
                  <div className="w-8 h-8 flex justify-start items-center gap-4">
                    <button
                      onClick={onBookmarkClick}
                      className="flex-1 self-stretch p-1 bg-stone-50 rounded-full flex justify-center items-center gap-2.5"
                    >
                      <div className="w-6 h-6 relative">
                        <div className="w-6 h-6 left-0 top-0 absolute rounded-full overflow-hidden">
                          <div className="w-4 h-3.5 left-[3.99px] top-[5.01px] absolute bg-fuchsia-600" />
                        </div>
                      </div>
                    </button>
                  </div>
                )}
              </div>)}

          </div>
        )}
      </div>
    </div>
  );
};

export default CustomCard;